import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { formatarDinheiro } from '@/pages/gerenciar/utils';

interface Props {
  eventoId: number;
  loja: string | null;
  pix: string | null;
  cartao: string | null;
  parcelado: string | null;
  aoSalvo: () => void;
}

type Quem = 'organizador' | 'comprador';

interface OpcaoTaxa {
  modo: 'plataforma' | 'organizador' | 'comprador';
  atual: boolean;
  recomendado: boolean;
  rotulo: string;
  frase: string;
  comprador_paga_centavos: number;
  organizador_recebe_centavos: number;
  se_liga_recebe_centavos: number;
  mercado_pago_centavos: number;
}

interface SimulacaoTaxas {
  valor_base_centavos: number;
  taxa_servico_centavos: number;
  atual: string;
  opcoes: OpcaoTaxa[];
}

interface LinhaTaxa {
  chave: 'loja' | 'pix' | 'cartao' | 'parcelado';
  rotulo: string;
  emBreve?: boolean;
  tipo: 'fichas' | 'ingresso';
}

const LINHAS: LinhaTaxa[] = [
  { chave: 'loja', rotulo: 'Fichas', tipo: 'fichas' },
  { chave: 'pix', rotulo: 'Ingressos · Pix', tipo: 'ingresso' },
  { chave: 'cartao', rotulo: 'Cartão à vista', emBreve: true, tipo: 'ingresso' },
  { chave: 'parcelado', rotulo: 'Parcelado', emBreve: true, tipo: 'ingresso' },
];

// Fichas guardam 'comprador'|'organizador' como sempre. Ingressos lêem
// 'organizador' de um lado e qualquer outro valor ('comprador' ou 'plataforma')
// como o lado "Comprador".
function valorDe(chave: string, valores: Props): Quem {
  if (chave === 'loja') {
    return valores.loja === 'comprador' ? 'comprador' : 'organizador';
  }
  const v = chave === 'pix' ? valores.pix : chave === 'cartao' ? valores.cartao : valores.parcelado;
  return v === 'organizador' ? 'organizador' : 'comprador';
}

function colunaDe(chave: string): string {
  if (chave === 'loja') return 'loja_quem_paga_taxa';
  if (chave === 'pix') return 'quem_paga_taxa';
  if (chave === 'cartao') return 'quem_paga_taxa_cartao';
  return 'quem_paga_taxa_parcelado';
}

// Quem paga a taxa — fichas e ingressos. Grava direto em eventos.
// Ingressos nunca gravam 'comprador': o lado "Comprador" vira 'plataforma'.
export default function CartaoTaxas({ eventoId, loja, pix, cartao, parcelado, aoSalvo }: Props) {
  const [simulacao, setSimulacao] = useState<SimulacaoTaxas | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const valores: Props = { eventoId, loja, pix, cartao, parcelado, aoSalvo };

  const carregarSimulacao = useCallback(async () => {
    const { data, error } = await supabase.rpc('simular_taxas', { p_evento: eventoId });
    if (error) {
      setErro(mensagemAmigavel(error));
      return;
    }
    setSimulacao((data as SimulacaoTaxas) ?? null);
  }, [eventoId]);

  useEffect(() => {
    carregarSimulacao();
  }, [carregarSimulacao]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function salvar(chave: string, quem: Quem) {
    setSalvando(chave);
    setErro(null);
    const coluna = colunaDe(chave);
    // Fichas mantêm o valor de sempre; ingressos gravam 'plataforma' no lado Comprador.
    const valorGravado = chave === 'loja' ? quem : quem === 'comprador' ? 'plataforma' : 'organizador';
    try {
      const { error } = await supabase
        .from('eventos')
        .update({ [coluna]: valorGravado })
        .eq('id', eventoId);
      if (error) throw error;

      if (chave !== 'loja') {
        const modo = quem === 'comprador' ? 'plataforma' : 'organizador';
        const opcao = simulacao?.opcoes?.find((o) => o.modo === modo) ?? null;
        const paga = opcao?.comprador_paga_centavos;
        setToast(
          paga != null ? `Salvo. Novas compras já saem com ${formatarDinheiro(paga)}.` : 'Salvo.',
        );
      } else {
        setToast('Salvo.');
      }

      await carregarSimulacao();
      aoSalvo();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(null);
    }
  }

  return (
    <div className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <h2 className="px-1 text-[17px] font-bold text-foreground-950">Quem paga a taxa?</h2>

      {erro && <p className="mt-2 px-1 text-[14px] text-danger-600">{erro}</p>}

      <div className="mt-3 space-y-5">
        {LINHAS.map((linha) => {
          const quem = valorDe(linha.chave, valores);
          const salvandoEsta = salvando === linha.chave;
          const modo = quem === 'comprador' ? 'plataforma' : 'organizador';
          const opcaoSelecionada = simulacao?.opcoes?.find((o) => o.modo === modo) ?? null;
          const opcaoPlataforma = simulacao?.opcoes?.find((o) => o.modo === 'plataforma') ?? null;
          const opcaoOrganizador = simulacao?.opcoes?.find((o) => o.modo === 'organizador') ?? null;

          const subOrganizador =
            linha.tipo === 'ingresso' && opcaoOrganizador
              ? `recebe ${formatarDinheiro(opcaoOrganizador.organizador_recebe_centavos)}`
              : 'o preço anunciado é o cobrado';
          const subComprador =
            linha.tipo === 'ingresso' && opcaoPlataforma
              ? `paga ${formatarDinheiro(opcaoPlataforma.comprador_paga_centavos)}`
              : 'preço + 6% de taxa de serviço';

          return (
            <div key={linha.chave}>
              <div className="flex items-center justify-between gap-2 px-1">
                <p className="text-[15px] font-semibold text-foreground-950">{linha.rotulo}</p>
                {linha.emBreve && (
                  <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[12px] font-semibold text-foreground-600">
                    em breve
                  </span>
                )}
              </div>

              {/* Controle segmentado de largura total */}
              <div className="mt-2 flex rounded-2xl bg-background-100 p-1">
                <button
                  type="button"
                  onClick={() => salvar(linha.chave, 'organizador')}
                  disabled={salvandoEsta}
                  className={`flex min-h-[52px] min-w-0 flex-1 cursor-pointer flex-col items-center justify-center rounded-xl px-2 text-center transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    quem === 'organizador'
                      ? 'bg-white text-foreground-950 shadow-[0_3px_10px_rgba(20,25,60,.08)]'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  <span className="text-[14px] font-bold leading-tight">Organizador</span>
                  <span className="mt-0.5 text-[12px] leading-tight text-foreground-500">
                    {subOrganizador}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => salvar(linha.chave, 'comprador')}
                  disabled={salvandoEsta}
                  className={`flex min-h-[52px] min-w-0 flex-1 cursor-pointer flex-col items-center justify-center rounded-xl px-2 text-center transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    quem === 'comprador'
                      ? 'bg-white text-foreground-950 shadow-[0_3px_10px_rgba(20,25,60,.08)]'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  <span className="text-[14px] font-bold leading-tight">Comprador</span>
                  <span className="mt-0.5 text-[12px] leading-tight text-foreground-500">
                    {subComprador}
                  </span>
                </button>
              </div>

              {linha.tipo === 'fichas' ? (
                <p className="mt-1.5 px-1 text-[13px] text-foreground-500">
                  {quem === 'organizador'
                    ? 'O preço anunciado é o cobrado.'
                    : 'O comprador paga o preço + 6% de taxa de serviço (somada no Pix).'}
                </p>
              ) : (
                opcaoSelecionada?.frase && (
                  <p className="mt-1.5 px-1 text-[13px] text-foreground-500">
                    {opcaoSelecionada.frase}
                    {linha.chave !== 'pix' && (
                      <span className="mt-0.5 block text-foreground-400">
                        Valores do Pix; o cartão terá a taxa do cartão.
                      </span>
                    )}
                  </p>
                )
              )}
            </div>
          );
        })}
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-2xl bg-foreground-950 px-4 py-3 text-[14px] text-white">
          {toast}
        </div>
      )}
    </div>
  );
}